import React, { Fragment, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { GridLayout } from 'components/grid/GridLayout';
import { FormDateInput, FormHeading, FormHelperText, FormLocationSelector, FormMultiSelect, FormNumericInput, FormRadio, FormSelect, FormSwitch, FormTextInput } from 'components/form-fields';
import { ShowHide } from 'components/layout';
import { AlertConfirm } from 'components/notifications';
import {
    isLeavingScheduling,
    isReturningToScheduling,
    scheduleModeDescriptions,
    scheduleModeDisplayOrder,
    showsAdvanceNotice
} from 'pages/employee-reviews/review-types/shared/reviewTypeEditorHelpers';
import { ReviewTypeScheduleStartModal } from 'pages/employee-reviews/review-types/shared/ReviewTypeScheduleStartModal';

const defaultObject = {};

const withdrawnConfirmText = "Turning this review type off will remove its scheduled pending reviews when you save, and no further reviews will be scheduled or raised against it. Completed reviews are kept.";
const raisedOnDemandConfirmText = "Turning scheduling off will remove this review type's scheduled pending reviews when you save. It stays available to raise manually. Completed reviews are kept.";
const scheduledHelperText = "Turn off for a review type that is only raised on demand.";
const inactiveScheduleHelperText = "An inactive review type can't be scheduled. Switch it on to turn scheduling on.";
const allowCustomNextReviewDateHelperText = "Let the manager set the next review date when they complete a review. Without a schedule, this is how the next review is set.";

export function ReviewTypeDetailsStep(
    {
        reviewType,
        onUpdate,
        errors = defaultObject,
        employeeOptions = defaultObject,
        reviewOptions = defaultObject,
        loading = false,
        saving = false,
        createMode = false,
        startDateLocked = false,
        originalReviewType = null
    }
) {

    const [useHierarchy, setUseHierarchy] = useState(true);
    const [reviewTypeAwaitingStartDate, setReviewTypeAwaitingStartDate] = useState(null);
    const [reviewTypeAwaitingDeactivation, setReviewTypeAwaitingDeactivation] = useState(null);
    const [reviewTypeAwaitingUnscheduling, setReviewTypeAwaitingUnscheduling] = useState(null);

    function onScheduleStartConfirm(startDate) {
        onUpdate({
            ...reviewTypeAwaitingStartDate,
            schedule: {
                ...reviewTypeAwaitingStartDate.schedule,
                startDate: startDate
            }
        });
        setReviewTypeAwaitingStartDate(null);
    }

    function onScheduleStartCancel() {
        setReviewTypeAwaitingStartDate(null);
    }

    function onDeactivationConfirm() {
        onUpdate(reviewTypeAwaitingDeactivation);
        setReviewTypeAwaitingDeactivation(null);
    }

    function onDeactivationCancel() {
        setReviewTypeAwaitingDeactivation(null);
    }

    function onUnschedulingConfirm() {
        onUpdate(reviewTypeAwaitingUnscheduling);
        setReviewTypeAwaitingUnscheduling(null);
    }

    function onUnschedulingCancel() {
        setReviewTypeAwaitingUnscheduling(null);
    }

    function onNameChange(event) {
        onUpdate({
            ...reviewType,
            name: event.target.value
        });
    }

    // An inactive review type is never scheduled, so switching it off takes the schedule off
    // with it. Switching it back on leaves the schedule off to be turned on separately.
    // Switching off a saved scheduled review type removes its pending reviews on save, so the
    // switch is held until the user confirms.
    function onEnabledChange(event) {
        if (event.target.checked) {
            onUpdate({
                ...reviewType,
                enabled: true
            });
            return;
        }

        const newReviewType = {
            ...reviewType,
            enabled: false,
            schedule: {
                ...reviewType.schedule,
                active: false
            }
        };

        if (isLeavingScheduling(newReviewType, originalReviewType)) {
            setReviewTypeAwaitingDeactivation(newReviewType);
            return;
        }

        onUpdate(newReviewType);
    }

    // A saved review type returning to scheduling restarts every employee's schedule from a
    // new start date, so the switch is held until a date is chosen. One leaving scheduling
    // has its pending reviews removed on save, so the switch is held until the user confirms.
    function onScheduleActiveChange(event) {
        const newReviewType = {
            ...reviewType,
            schedule: {
                ...reviewType.schedule,
                active: event.target.checked
            }
        };

        if (isReturningToScheduling(newReviewType, originalReviewType)) {
            setReviewTypeAwaitingStartDate(newReviewType);
            return;
        }

        if (isLeavingScheduling(newReviewType, originalReviewType)) {
            setReviewTypeAwaitingUnscheduling(newReviewType);
            return;
        }

        onUpdate(newReviewType);
    }

    function onAllowCustomNextReviewDateChange(event) {
        onUpdate({
            ...reviewType,
            allowCustomNextReviewDate: event.target.checked
        });
    }

    function onRaiseAlertWhenPendingChange(event) {
        onUpdate({
            ...reviewType,
            raiseAlertWhenPending: event.target.checked
        });
    }

    function onScheduleFieldChange(value, fieldName) {
        onUpdate({
            ...reviewType,
            schedule: {
                ...reviewType.schedule,
                [fieldName]: value
            }
        });
    }

    function onStartDateChange(item) {
        onScheduleFieldChange(item ? moment(item).format("YYYY-MM-DD") : null, "startDate");
    }

    function onScheduleNumericChange(value, fieldName) {
        onScheduleFieldChange(value === "" ? null : value, fieldName);
    }

    function onEmployeeMultiSelectChange(item, name) {
        const newInstance = {
            ...reviewType
        };

        if (!newInstance[name].some((current) => current.id === item.id)) {
            newInstance[name] = [...newInstance[name], item];
        }

        onUpdate(newInstance);
    }

    function onEmployeeRemove(itemName, name) {
        onUpdate({
            ...reviewType,
            [name]: reviewType[name].filter((current) => current.name !== itemName)
        });
    }

    function onFilterByLocationsChange(event) {
        // The selection is deliberately kept when the filter is switched off so
        // re-enabling it restores the previous locations; buildApiModel strips
        // the locations from the save payload while the filter is off.
        onUpdate({
            ...reviewType,
            filterByLocations: event.target.checked
        });
    }

    function onSelectedLocationsChange(selectedLocations) {
        onUpdate({
            ...reviewType,
            employeeLocations: selectedLocations
        });
    }

    let startDateHelperText = null;

    if (startDateLocked) {
        startDateHelperText = "Reviews have already been completed against this review type, so the start date can no longer be changed.";
    }

    const reviewTypeEnabled = reviewType?.enabled === true;
    const advanceNoticeShown = showsAdvanceNotice(reviewType);

    let scheduleHelperText = scheduledHelperText;

    if (!reviewTypeEnabled) {
        scheduleHelperText = inactiveScheduleHelperText;
    }

    // Only a new review type pins the earliest selectable date to today. An existing one can
    // arrive holding a past start date, which the validation rule rejects on a return to
    // scheduling, so the picker itself stays unrestricted.
    const startDateMinimum = useMemo(() => {
        if (!createMode) {
            return null;
        }

        return moment().startOf('day').toDate();
    }, [createMode]);

    // The options are laid out in the editor's own order rather than the order the lookup
    // returns them in, and a mode the lookup does not return is not offered.
    const scheduleModeOptions = useMemo(() => {
        const scheduleModes = reviewOptions?.scheduleModes ?? [];

        return scheduleModeDisplayOrder
            .map((modeKey) => scheduleModes.find((mode) => mode.key === modeKey))
            .filter((mode) => mode != null)
            .map((mode) => {
                return {
                    value: mode.key,
                    label: mode.name,
                    helperText: scheduleModeDescriptions[mode.key]
                };
            });
    }, [reviewOptions]);

    return (
        <div>
            <h2>Details &amp; Schedule</h2>
            <FormTextInput
                id="review-type-name"
                headingText="Review type name"
                loading={loading}
                disabled={saving}
                value={reviewType?.name}
                maxLength={200}
                onChange={onNameChange}
                dangerHelperText={errors.name}
            />
            <FormDateInput
                id="review-type-start-date"
                headingText="Start date"
                loading={loading}
                disabled={saving || startDateLocked}
                value={reviewType?.schedule?.startDate}
                minDate={startDateMinimum}
                onChange={onStartDateChange}
                helperText={startDateHelperText}
                dangerHelperText={errors["schedule.startDate"]}
            />
            <FormSwitch
                id="review-type-active"
                headingText="Active"
                checked={reviewType?.enabled}
                onChange={onEnabledChange}
                loading={loading}
                disabled={saving}
            />
            <h3>Employees</h3>
            <FormHelperText loading={loading}>Select the types of employee this review applies to.</FormHelperText>
            {/* Row gap stays 0 so vertical rhythm comes from the fields' own standard margins. */}
            <GridLayout numberOfColumns={3} gap={[0, 20]}>
                    <FormMultiSelect
                        loading={loading}
                        disabled={saving}
                        items={employeeOptions?.employeeTypes}
                        onItemSelect={(item) => onEmployeeMultiSelectChange(item, "employeeTypes")}
                        onRemove={(item) => onEmployeeRemove(item, "employeeTypes")}
                        placeholder="Please select"
                        headingText="Employee Type"
                        dangerHelperText={errors.employeeTypes}
                        selectedValues={reviewType?.employeeTypes ? reviewType.employeeTypes : []}
                    />
                    <FormMultiSelect
                        loading={loading}
                        disabled={saving}
                        items={employeeOptions?.employeeCategories}
                        onItemSelect={(item) => onEmployeeMultiSelectChange(item, "employeeCategories")}
                        onRemove={(item) => onEmployeeRemove(item, "employeeCategories")}
                        placeholder="Please select"
                        headingText="Employee Category"
                        dangerHelperText={errors.employeeCategories}
                        selectedValues={reviewType?.employeeCategories ? reviewType.employeeCategories : []}
                    />
                    <FormMultiSelect
                        loading={loading}
                        disabled={saving}
                        items={employeeOptions?.employeeStatuses}
                        onItemSelect={(item) => onEmployeeMultiSelectChange(item, "employeeStatuses")}
                        onRemove={(item) => onEmployeeRemove(item, "employeeStatuses")}
                        placeholder="Please select"
                        headingText="Employee Status"
                        dangerHelperText={errors.employeeStatuses}
                        selectedValues={reviewType?.employeeStatuses ? reviewType.employeeStatuses : []}
                    />
                    <FormSwitch
                        id="review-type-filter-by-locations"
                        headingText="Filter by locations"
                        checked={reviewType?.filterByLocations}
                        onChange={onFilterByLocationsChange}
                        loading={loading}
                        disabled={saving}
                    />
                    {/* Location defaults are switched off so the selection is always exactly
                        the review type's saved locations — a newly enabled filter starts with
                        zero locations selected. */}
                    <ShowHide
                        evaluator={reviewType?.filterByLocations === true}
                        show={(
                            <FormLocationSelector
                                headingText="Locations"
                                businessArea={"Tacho"}
                                loading={loading}
                                selectedLocations={reviewType?.employeeLocations ? reviewType.employeeLocations : []}
                                setSelectedLocations={onSelectedLocationsChange}
                                useLocationDefaults={false}
                                useHierarchy={useHierarchy}
                                setUseHierarchy={setUseHierarchy}
                                locationDefaultsToTrue={false}
                                dangerHelperText={errors.employeeLocations}
                            />
                        )}
                    />
            </GridLayout>
            <h3>Schedule</h3>
            <FormSwitch
                id="review-type-scheduled"
                headingText="Schedule this review"
                checked={reviewType?.schedule?.active}
                onChange={onScheduleActiveChange}
                helperText={scheduleHelperText}
                loading={loading}
                disabled={saving || !reviewTypeEnabled}
            />
            <ShowHide
                evaluator={reviewType?.schedule?.active === true}
                show={(
                    <Fragment>
                        <FormRadio
                            id="review-type-schedule-mode"
                            headingText="Schedule type"
                            options={scheduleModeOptions}
                            selectedValue={reviewType?.schedule?.mode}
                            loading={loading}
                            disabled={saving}
                            onChange={(event) => onScheduleFieldChange(event.currentTarget.value, "mode")}
                        />
                        <div className="form-field">
                            <div className="inline-items">
                                <div>Review every</div>
                                <FormNumericInput
                                    id="review-type-frequency"
                                    min={1}
                                    max={1000}
                                    loading={loading}
                                    disabled={saving}
                                    selectedValue={reviewType?.schedule?.frequency}
                                    onValueChange={(value) => onScheduleNumericChange(value, "frequency")}
                                />
                                <FormSelect
                                    items={reviewOptions?.frequencyUnits}
                                    loading={loading}
                                    disabled={saving}
                                    placeholder="Choose a unit"
                                    selectedValue={reviewType?.schedule?.frequencyUnitId}
                                    onItemSelect={(item) => onScheduleFieldChange(item.id, "frequencyUnitId")}
                                    small
                                />
                            </div>
                            {/* One message covers the whole row, and it sits outside the inline
                                row so the row's vertical alignment doesn't shift while showing. */}
                            <FormHelperText loading={loading} danger={true}>{errors["schedule.frequency"] ?? errors["schedule.frequencyUnitId"]}</FormHelperText>
                        </div>
                    </Fragment>
                )}
            />
            <ShowHide
                evaluator={reviewTypeEnabled}
                show={(
                    <FormSwitch
                        id="review-type-allow-custom-next-review-date"
                        headingText="Allow custom next review date"
                        helperText={allowCustomNextReviewDateHelperText}
                        checked={reviewType?.allowCustomNextReviewDate}
                        onChange={onAllowCustomNextReviewDateChange}
                        loading={loading}
                        disabled={saving}
                    />
                )}
            />
            <ShowHide
                evaluator={advanceNoticeShown}
                show={(
                    <div className="form-field">
                        <FormHeading loading={loading} headingLevel={"h4"}>Advance notice</FormHeading>
                        <FormHelperText loading={loading}>How long before the next review date the review appears as pending and its alert is raised. Applies to scheduled reviews and to next review dates set when a review is completed.</FormHelperText>
                        <div className="inline-items">
                            <div>Show</div>
                            <FormNumericInput
                                id="review-type-advance-notice-value"
                                min={1}
                                max={1000}
                                loading={loading}
                                disabled={saving}
                                selectedValue={reviewType?.schedule?.advanceNoticeValue}
                                onValueChange={(value) => onScheduleNumericChange(value, "advanceNoticeValue")}
                            />
                            <FormSelect
                                items={reviewOptions?.advanceNoticeUnits}
                                loading={loading}
                                disabled={saving}
                                placeholder="Choose a unit"
                                selectedValue={reviewType?.schedule?.advanceNoticeUnitId}
                                onItemSelect={(item) => onScheduleFieldChange(item.id, "advanceNoticeUnitId")}
                                small
                            />
                            <div>before the review is due</div>
                        </div>
                        {/* One message covers the whole row, and it sits outside the inline
                            row so the row's vertical alignment doesn't shift while showing. */}
                        <FormHelperText loading={loading} danger={true}>{errors["schedule.advanceNoticeValue"] ?? errors["schedule.advanceNoticeUnitId"] ?? errors["schedule.advanceNotice"]}</FormHelperText>
                    </div>
                )}
            />
            <FormSwitch
                id="review-type-raise-alert-when-pending"
                headingText="Raise an alert for pending reviews"
                checked={reviewType?.raiseAlertWhenPending}
                onChange={onRaiseAlertWhenPendingChange}
                helperText="Raise an alert when an employee has a review pending."
                loading={loading}
                disabled={saving}
            />
            <ReviewTypeScheduleStartModal
                isOpen={reviewTypeAwaitingStartDate != null}
                startDate={reviewType?.schedule?.startDate}
                mode={reviewType?.schedule?.mode}
                onCancel={onScheduleStartCancel}
                onConfirm={onScheduleStartConfirm}
            />
            <AlertConfirm
                isOpen={reviewTypeAwaitingDeactivation != null}
                title="Turn off this review type?"
                confirmButtonText="Turn off review type"
                onConfirm={onDeactivationConfirm}
                onCancel={onDeactivationCancel}
            >
                <p>{withdrawnConfirmText}</p>
            </AlertConfirm>
            <AlertConfirm
                isOpen={reviewTypeAwaitingUnscheduling != null}
                title="Turn off scheduling?"
                confirmButtonText="Turn off scheduling"
                onConfirm={onUnschedulingConfirm}
                onCancel={onUnschedulingCancel}
            >
                <p>{raisedOnDemandConfirmText}</p>
            </AlertConfirm>
        </div>
    );
}

ReviewTypeDetailsStep.propTypes = {
    reviewType: PropTypes.object.isRequired,
    onUpdate: PropTypes.func.isRequired,
    errors: PropTypes.object,
    employeeOptions: PropTypes.object,
    reviewOptions: PropTypes.object,
    loading: PropTypes.bool,
    saving: PropTypes.bool,
    createMode: PropTypes.bool,
    startDateLocked: PropTypes.bool,
    originalReviewType: PropTypes.object
};
